import React, { act, useContext, useState } from 'react';
import { StyleSheet, View, Linking } from 'react-native';
import PropTypes from 'prop-types';
import { H1, H2, H3, H4, Link, Paragraph } from '../text';
import Button from '../common/Button';
import CdnImageBackground from '../common/CdnImageBackground';
import ComponentContainer from '../common/ComponentContainer';
import { HorizontalRule } from '../common/HorizontalRule';
import { StyledWebView } from '../common/StyledWebView';
import LayoutContext from '../../contexts/Layout';

const STYLED_WEB_VIEW_ID = 'job';

const Job = ({
    ctaButtonText,
    ctaLink,
    department,
    featuredImage,
    followContentLink,
    jobDescription,
    jobLocation,
    jobTitle,
    jobType,
    map,
    nodeTypeAlias = 'Job',
    physicalWidth,
    qualifications,
    responsibilities,
    width
}) => {
    const { layout } = useContext(LayoutContext);
    const [imageDimensions, setImageDimensions] = useState();
    const actualWidth = physicalWidth - layout.styleGuide.spacer1 * 2;

    const CtaButton = () => (
        <Button
            onPress={async () => await followContentLink(ctaLink.isExternal, ctaLink.internalKey, ctaLink.externalUrl)}
            style={{ backgroundColor: '#a61a2b', maxWidth: 200 }}
            text={ctaButtonText}
            textStyle={{ color: '#fff' }}
        />
    );

    const renderValue = (label, value, isMap = false) => {
        let content = <Paragraph text={value} />;

        if (isMap && map) {
            content = <Link onPress={() => Linking.openURL(value)} text='View Map & Directions' />;
        }

        return (
            <View style={StyleSheet.flatten(Styles.valueWrapper, { marginVertical: layout.styleGuide.spacer3 })}>
                <View style={{ flex: 0.25 }}>
                    <H4 text={label} />
                </View>
                <View style={{ flex: 1 }}>{content}</View>
            </View>
        );
    };

    return (
        <View
            style={{ flexDirection: 'row', marginBottom: layout.styleGuide.componentSeparator }}
            dataSet={{ umbracoComponentName: nodeTypeAlias, spacerBottom: `StyleGuide.ComponentSeparator(${layout.styleGuide.componentSeparator})` }}
        >
            <View style={{ width }}>
                <CdnImageBackground imageDimensions={imageDimensions} setImageDimensions={setImageDimensions} uri={featuredImage.cdnImagePath} width={physicalWidth}>
                    <View style={{ bottom: 10, left: layout.styleGuide.spacer1, position: 'absolute' }}>
                        <H1 style={{ color: '#fff' }} text={jobTitle} />
                    </View>
                </CdnImageBackground>

                <View style={{ backgroundColor: '#f7f8f9' }}>
                    <View style={{ marginBottom: layout.styleGuide.spacer2, marginHorizontal: layout.styleGuide.spacer1 }}>
                        <H3 style={{ marginVertical: layout.styleGuide.spacer3 }} text={department} />
                        <CtaButton />
                    </View>
                </View>

                <ComponentContainer>
                    <View style={{ marginBottom: layout.styleGuide.spacer2 }}>
                        <StyledWebView html={jobDescription} nodeId={STYLED_WEB_VIEW_ID} width={actualWidth} />
                    </View>

                    <HorizontalRule />

                    <View style={{ flexDirection: 'row', marginTop: layout.styleGuide.spacer2 }}>
                        <View style={{ width: actualWidth }}>
                            <View style={{ flexDirection: 'row' }}>
                                <H2 style={{ fontWeight: 900, marginBottom: layout.styleGuide.spacer3 }} text='About The Role' />
                            </View>
                            {renderValue('Type:', jobType)}
                            {renderValue('Location:', jobLocation)}
                            {renderValue('Map:', map, true)}
                            {renderValue('Department:', department)}
                            {!!responsibilities && (
                                <>
                                    <H2 style={{ fontWeight: 900, marginBottom: layout.styleGuide.spacer3, marginTop: layout.styleGuide.spacer2 }} text='Responsibilities' />
                                    <View>
                                        <StyledWebView html={responsibilities} nodeId={STYLED_WEB_VIEW_ID} width={actualWidth} />
                                    </View>
                                </>
                            )}
                            {!!qualifications && (
                                <>
                                    <H2 style={{ fontWeight: 900, marginVertical: layout.styleGuide.spacer3, marginTop: layout.styleGuide.spacer2 }} text='Qualifications' />
                                    <View>
                                        <StyledWebView html={qualifications} nodeId={STYLED_WEB_VIEW_ID} width={actualWidth} />
                                    </View>
                                </>
                            )}
                            <View style={{ flexDirection: 'row', marginTop: layout.styleGuide.spacer3 }}>
                                <CtaButton />
                            </View>
                        </View>
                    </View>
                </ComponentContainer>
            </View>
        </View>
    );
};

const Styles = StyleSheet.create({
    ctaButton: {
        backgroundColor: '#a61a2b',
        padding: 10,
        maxWidth: 150
    },
    valueWrapper: {
        alignItems: 'center',
        flexDirection: 'row'
    }
});

Job.propTypes = {
    featuredImage: PropTypes.shape({
        cdnImagePath: PropTypes.string
    }),
    jobTitle: PropTypes.string,
    jobDescription: PropTypes.string,
    ctaLink: PropTypes.shape({
        internalKey: PropTypes.string,
        isExternal: PropTypes.bool.isRequired,
        externalUrl: PropTypes.string
    }),
    ctaButtonText: PropTypes.string,
    jobType: PropTypes.string,
    jobLocation: PropTypes.string,
    map: PropTypes.string,
    department: PropTypes.string,
    responsibilities: PropTypes.string,
    qualifications: PropTypes.string,
    width: PropTypes.oneOf([PropTypes.number, PropTypes.string]).isRequired
};

export default Job;
